import { addComment, deleteComment, getTreeComments, likeComment, unlikeComment, updateComment } from "api/comments";
import { getTreeEntities, getTreeRelations } from "api/entities";
import { getSubscriptionStatus } from "api/subscriptions";
import { getTreeById, likeTree, starTree, unlikeTree, unstarTree } from "api/trees";
import type { CommentDto, EntityDto, TreeDto, TreeRelationDto } from "api/types";
import {
	CollaboratorsDialog,
	CollaboratorStack,
	RelationGroupChips,
	Spinner,
	TreeAccessNotice,
	type TreeAccessNoticeProps,
	UserAvatar
} from "components";
import { fieldLimits } from "config";
import { useRelationGroup } from "hooks/useRelationGroup";
import { useTreeSeo } from "hooks/useTreeSeo";
import React, { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Paths, { getTreeEditPath, getTreeFullViewPath, getTreeViewPath, getUserProfilePath } from "routes/Paths";
import { getSignInPathWithRedirect } from "routes/signInRedirect";
import { useSession } from "Session";
import { getTreeShareUrl, startRequest } from "utilities";

import CommentItem from "./CommentItem";
import EditTreeDialog from "./EditTreeDialog";
import PublishMenu from "./PublishMenu";
import ReportTreeDialog from "./ReportTreeDialog";
import ShareTreeDialog from "./ShareTreeDialog";
import TopNav from "./TopNav";
import TreeGraph from "./TreeGraph";
import ViewIcon, { type ViewIconName } from "./ViewIcon";

import styles from "./TreeViewPage.module.css";

type ApiError = {
	status?: number;
};

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

const getVisibilityIcon = (visibility: string): ViewIconName => {
	if (visibility === "Public") {
		return "globe";
	}

	if (visibility === "Draft") {
		return "pencil";
	}

	return "lock";
};

const canEditRole = (role: string | null | undefined): boolean => role === "Editor" || role === "Admin";

const formatCount = (value: number): string => {
	if (value >= 1000) {
		return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
	}

	return value.toString();
};

const countComments = (comments: CommentDto[]): number =>
	comments.reduce((total: number, comment: CommentDto) => total + 1 + comment.replies.length, 0);

/** Applies a patch to one comment anywhere in the two-level thread, returning a new list. */
const patchComment = (
	comments: CommentDto[],
	commentId: string,
	patch: (comment: CommentDto) => CommentDto
): CommentDto[] =>
	comments.map((comment: CommentDto) => {
		if (comment.id === commentId) {
			return patch(comment);
		}

		if (comment.replies.some((reply: CommentDto) => reply.id === commentId)) {
			return {
				...comment,
				replies: comment.replies.map((reply: CommentDto) => (reply.id === commentId ? patch(reply) : reply))
			};
		}

		return comment;
	});

const setLiked =
	(liked: boolean) =>
	(comment: CommentDto): CommentDto => ({
		...comment,
		isLikedByMe: liked,
		likeCount: Math.max(0, comment.likeCount + (liked ? 1 : -1))
	});

/** Removes a comment from the two-level thread: replies leave their parent, top-level comments take their thread. */
const removeComment = (comments: CommentDto[], target: CommentDto): CommentDto[] =>
	target.parentCommentId
		? comments.map((comment: CommentDto) =>
				comment.id === target.parentCommentId
					? { ...comment, replies: comment.replies.filter((reply: CommentDto) => reply.id !== target.id) }
					: comment
		  )
		: comments.filter((comment: CommentDto) => comment.id !== target.id);

const TreeViewPage = () => {
	const { t } = useTranslation("homePage");
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { getAccessToken, hasSession, signOut, user } = useSession();
	const [tree, setTree] = useState<TreeDto | null>(null);
	const [accessError, setAccessError] = useState<TreeAccessNoticeProps["reason"] | null>(null);
	useTreeSeo(tree, id);
	const [entities, setEntities] = useState<EntityDto[]>([]);
	const [relations, setRelations] = useState<TreeRelationDto[]>([]);
	// The tree arrives one round trip ahead of its entities, so the canvas has its own loading window
	// after the page chrome is already on screen.
	const [isEntityFetchPending, setIsEntityFetchPending] = useState(true);
	// Which relation context the canvas draws, remembered per tree for this viewer.
	const { relationGroup, setRelationGroup } = useRelationGroup(id);
	const [comments, setComments] = useState<CommentDto[]>([]);
	const [commentBody, setCommentBody] = useState("");
	const [isPosting, setIsPosting] = useState(false);
	const [isStarred, setIsStarred] = useState(false);
	const [starCount, setStarCount] = useState(0);
	const [isStarPending, setIsStarPending] = useState(false);
	const [isLiked, setIsLiked] = useState(false);
	const [likeCount, setLikeCount] = useState(0);
	const [isLikePending, setIsLikePending] = useState(false);
	const [isLinkCopied, setIsLinkCopied] = useState(false);
	const copiedTimer = useRef<number | undefined>(undefined);
	const [isCommentsOpen, setIsCommentsOpen] = useState(false);
	const [isEditOpen, setIsEditOpen] = useState(false);
	const [isShareOpen, setIsShareOpen] = useState(false);
	const [isReportOpen, setIsReportOpen] = useState(false);
	const [isCollaboratorsOpen, setIsCollaboratorsOpen] = useState(false);
	const [canCreatePrivateTrees, setCanCreatePrivateTrees] = useState(false);

	// Selecting an entity (or the canvas background) opens the full-page read-only view.
	const onSelectEntity = useCallback(
		(entity: EntityDto): void => {
			if (id) {
				navigate(getTreeFullViewPath(id, entity.id));
			}
		},
		[id, navigate]
	);
	const onCanvasBackgroundClick = useCallback((): void => {
		if (id) {
			navigate(getTreeFullViewPath(id));
		}
	}, [id, navigate]);

	// Every sign-in prompt on this page returns the visitor to the tree they were reading, so starring,
	// liking or commenting resumes where they left off instead of dropping them on the homepage.
	const goToSignIn = useCallback(
		(): void => navigate(id ? getSignInPathWithRedirect(getTreeViewPath(id)) : Paths.signIn),
		[id, navigate]
	);
	const goToEdit = useCallback((): void => {
		if (id) {
			navigate(getTreeEditPath(id));
		}
	}, [id, navigate]);
	const openComments = useCallback((): void => setIsCommentsOpen(true), []);
	const closeComments = useCallback((): void => setIsCommentsOpen(false), []);
	const openShareDialog = useCallback((): void => setIsShareOpen(true), []);
	const closeShareDialog = useCallback((): void => setIsShareOpen(false), []);
	const openReportDialog = useCallback((): void => setIsReportOpen(true), []);
	const closeReportDialog = useCallback((): void => setIsReportOpen(false), []);
	const openCollaborators = useCallback((): void => setIsCollaboratorsOpen(true), []);
	const closeCollaborators = useCallback((): void => setIsCollaboratorsOpen(false), []);
	const openEditDialog = useCallback(async (): Promise<void> => {
		setIsEditOpen(true);

		// Pro entitlement gates the Private option in the dialog; failure just leaves it locked.
		const token = await getAccessToken();
		if (!token) {
			return;
		}

		try {
			const status = await getSubscriptionStatus(token);
			setCanCreatePrivateTrees(status.canCreatePrivateTrees);
		} catch {
			// Ignore: keep Private gated by default.
		}
	}, [getAccessToken]);
	const closeEditDialog = useCallback((): void => setIsEditOpen(false), []);
	const onTreeSaved = useCallback((updated: TreeDto): void => setTree(updated), []);
	// The tree this page is showing no longer exists for the user, so replace the history entry —
	// Back must not return to a tree that now 404s.
	const onTreeDeleted = useCallback((): void => {
		setIsEditOpen(false);
		navigate(Paths.root, { replace: true });
	}, [navigate]);
	const onCommentChange = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>): void => setCommentBody(event.target.value),
		[]
	);

	useEffect(() => {
		if (!id) {
			return undefined;
		}

		let isCurrent = true;

		const load = async (): Promise<void> => {
			// Anonymous viewers have no token; public trees are readable without one.
			const token = await getAccessToken();
			if (!isCurrent) {
				return;
			}

			// Every request goes out at once. None of them needs anything the tree row returns, so
			// awaiting it first only bought the old `entityCount > 0` shortcut — two requests saved on
			// empty trees, paid for with an extra round trip on every populated one, which is the
			// expensive case. An empty tree now answers both calls with an empty list instead.
			const treeRequest = startRequest(getTreeById(id, token));
			const entitiesRequest = startRequest(getTreeEntities(id, token));
			const relationsRequest = startRequest(getTreeRelations(id, token));
			const commentsRequest = startRequest(getTreeComments(id, token).catch(() => [] as CommentDto[]));

			// The tree row is small and lands first; showing the page around it beats holding everything
			// back until the much larger entity payload arrives too.
			const loaded = await treeRequest;
			if (!isCurrent) {
				return;
			}

			setTree(loaded);
			setIsStarred(loaded.isStarredByMe);
			setStarCount(loaded.starCount);
			setIsLiked(loaded.isLikedByMe);
			setLikeCount(loaded.likeCount);

			const [treeEntities, treeRelations, treeComments] = await Promise.all([
				entitiesRequest,
				relationsRequest,
				commentsRequest
			]);

			if (!isCurrent) {
				return;
			}

			setEntities(treeEntities);
			setRelations(treeRelations);
			setComments(treeComments);
			setIsEntityFetchPending(false);
		};

		load().catch((error: unknown) => {
			const apiError = error as ApiError;

			if (hasSession && apiError.status === 401) {
				// Stale session: dropping it remounts the page anonymously, which retries the load.
				signOut();

				return;
			}

			if (!isCurrent) {
				return;
			}

			// The notice below replaces the canvas, but stop the spinner regardless so nothing is left
			// claiming to be busy.
			setIsEntityFetchPending(false);

			// A tree the visitor cannot open is a destination, not a detour: explain it on the tree's own
			// URL so the address stays shareable and a signed-out member can sign in from here. 403 means
			// the tree exists but is private or draft; anything else means it is gone for this visitor.
			setAccessError(apiError.status === 403 || apiError.status === 401 ? "noAccess" : "notFound");
		});

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken, hasSession, id, signOut]);

	const viewerInitials = useMemo(() => getInitials(user?.fullName ?? ""), [user?.fullName]);
	const commentCount = useMemo(() => countComments(comments), [comments]);

	const toggleStar = useCallback(async (): Promise<void> => {
		if (!id || isStarPending) {
			return;
		}

		// Starring requires an account — send anonymous visitors to sign in.
		if (!hasSession) {
			goToSignIn();

			return;
		}

		const token = await getAccessToken();
		if (!token) {
			return;
		}

		const next = !isStarred;
		setIsStarPending(true);
		setIsStarred(next);
		setStarCount((count: number) => Math.max(0, count + (next ? 1 : -1)));

		try {
			if (next) {
				await starTree(id, token);
			} else {
				await unstarTree(id, token);
			}
		} catch {
			// Revert the optimistic update on failure.
			setIsStarred(!next);
			setStarCount((count: number) => Math.max(0, count + (next ? -1 : 1)));
		} finally {
			setIsStarPending(false);
		}
	}, [getAccessToken, goToSignIn, hasSession, id, isStarred, isStarPending]);

	const toggleLike = useCallback(async (): Promise<void> => {
		if (!id || isLikePending) {
			return;
		}

		// Liking requires an account — send anonymous visitors to sign in.
		if (!hasSession) {
			goToSignIn();

			return;
		}

		const token = await getAccessToken();
		if (!token) {
			return;
		}

		const next = !isLiked;
		setIsLikePending(true);
		setIsLiked(next);
		setLikeCount((count: number) => Math.max(0, count + (next ? 1 : -1)));

		try {
			if (next) {
				await likeTree(id, token);
			} else {
				await unlikeTree(id, token);
			}
		} catch {
			// Revert the optimistic update on failure.
			setIsLiked(!next);
			setLikeCount((count: number) => Math.max(0, count + (next ? -1 : 1)));
		} finally {
			setIsLikePending(false);
		}
	}, [getAccessToken, goToSignIn, hasSession, id, isLiked, isLikePending]);

	const onShareClick = useCallback(async (): Promise<void> => {
		if (!id) {
			return;
		}

		// A public tree gets the API's share link, which is the one that previews as itself when pasted
		// into a chat — this bundle's meta tags are written by JavaScript, which link scrapers never run.
		// A non-public tree keeps its canonical URL: the share link would only show it the generic card,
		// and the person receiving it is a member who wants the real page.
		const link =
			tree?.visibility === "Public" ? getTreeShareUrl(id) : `${window.location.origin}${getTreeViewPath(id)}`;

		try {
			await navigator.clipboard.writeText(link);
		} catch {
			// Clipboard access denied; skip the toast so we do not claim a copy that never happened.
			return;
		}

		setIsLinkCopied(true);
		window.clearTimeout(copiedTimer.current);
		copiedTimer.current = window.setTimeout(() => setIsLinkCopied(false), 2000);
	}, [id, tree]);

	// Clear the pending toast timer when leaving the page.
	useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

	const onPostComment = useCallback(async (): Promise<void> => {
		const body = commentBody.trim();
		if (!id || !body || isPosting) {
			return;
		}

		const token = await getAccessToken();
		if (!token) {
			return;
		}

		setIsPosting(true);
		try {
			const created = await addComment(id, { body }, token);
			setComments((current: CommentDto[]) => [...current, created]);
			setCommentBody("");
		} catch {
			// Leave the draft in place so the user can retry.
		} finally {
			setIsPosting(false);
		}
	}, [commentBody, getAccessToken, id, isPosting]);

	const onToggleCommentLike = useCallback(
		async (comment: CommentDto): Promise<void> => {
			if (!id) {
				return;
			}

			// Liking requires an account — send anonymous visitors to sign in.
			if (!hasSession) {
				goToSignIn();

				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			const next = !comment.isLikedByMe;
			setComments((current: CommentDto[]) => patchComment(current, comment.id, setLiked(next)));

			try {
				if (next) {
					await likeComment(id, comment.id, token);
				} else {
					await unlikeComment(id, comment.id, token);
				}
			} catch {
				// Revert the optimistic update on failure.
				setComments((current: CommentDto[]) => patchComment(current, comment.id, setLiked(!next)));
			}
		},
		[getAccessToken, goToSignIn, hasSession, id]
	);

	const onPostReply = useCallback(
		async (parentCommentId: string, body: string): Promise<boolean> => {
			if (!id) {
				return false;
			}

			const token = await getAccessToken();
			if (!token) {
				return false;
			}

			try {
				const created = await addComment(id, { body, parentCommentId }, token);
				setComments((current: CommentDto[]) =>
					current.map((comment: CommentDto) =>
						comment.id === parentCommentId
							? { ...comment, replies: [...comment.replies, created] }
							: comment
					)
				);

				return true;
			} catch {
				return false;
			}
		},
		[getAccessToken, id]
	);

	const onEditComment = useCallback(
		async (comment: CommentDto, body: string): Promise<boolean> => {
			if (!id) {
				return false;
			}

			const token = await getAccessToken();
			if (!token) {
				return false;
			}

			try {
				const updated = await updateComment(id, comment.id, body, token);
				setComments((current: CommentDto[]) =>
					patchComment(current, comment.id, (existing: CommentDto) => ({
						...existing,
						body: updated.body,
						isEdited: true
					}))
				);

				return true;
			} catch {
				return false;
			}
		},
		[getAccessToken, id]
	);

	const onDeleteComment = useCallback(
		async (comment: CommentDto): Promise<void> => {
			if (!id) {
				return;
			}

			// eslint-disable-next-line no-alert
			if (!window.confirm(t("view.confirmDeleteComment"))) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			try {
				await deleteComment(id, comment.id, token);
				setComments((current: CommentDto[]) => removeComment(current, comment));
			} catch {
				// Keep the comment visible so the user can retry.
			}
		},
		[getAccessToken, id, t]
	);

	if (accessError) {
		return (
			<div className={styles.page}>
				<TopNav />
				<TreeAccessNotice
					hasSession={hasSession}
					reason={accessError}
					returnTo={id ? getTreeViewPath(id) : Paths.root}
				/>
			</div>
		);
	}

	// The tree itself is still in flight. Keep the page chrome so the visitor sees the app rather than
	// a white screen, and say what is happening.
	if (!tree) {
		return (
			<div className={styles.page}>
				<TopNav />
				<div className={styles.pageLoading}>
					<Spinner label={t("view.loadingTree")} />
				</div>
			</div>
		);
	}

	const canEdit = canEditRole(tree.myRole);
	// Editing tree metadata (and visibility) requires Admin on the backend; the owner is implicitly Admin.
	const canManage = tree.myRole === "Admin";
	const isOwner = Boolean(user?.id) && tree.ownerUserId === user?.id;
	// Drafts are not shareable; admins get a publish control to convert to Public or Private instead.
	const isDraft = tree.visibility === "Draft";
	const ownerName = tree.ownerName?.trim() || t("view.treeOwner");
	const collaborators = tree.collaborators ?? [];
	const hasEntities = entities.length > 0;
	// Gated on the count the tree itself reports: a tree the server says is empty goes straight to its
	// empty state rather than flashing a spinner, and the canvas never waits on the comments fetch
	// that shares the same request batch.
	const isCanvasLoading = isEntityFetchPending && tree.entityCount > 0;
	const commentsEnabled = tree.commentsEnabled;
	const shareUrl = id ? `${window.location.origin}${getTreeViewPath(id)}` : window.location.href;

	return (
		<div className={styles.page}>
			<TopNav />

			{isLinkCopied && (
				<div className={styles.copyToast} role="status">
					<ViewIcon name="share" />
					{t("view.linkCopied")}
				</div>
			)}

			<main className={styles.content}>
				<section className={styles.hero}>
					<div className={styles.heroCopy}>
						<div className={styles.heroTitleRow}>
							<h1 className={styles.heroTitle}>{tree.title}</h1>
							{canManage && (
								<button
									aria-label={t("view.editDetails")}
									className={styles.titleEditButton}
									onClick={openEditDialog}
									title={t("view.editDetails")}
									type="button">
									<ViewIcon name="pencil" />
								</button>
							)}
						</div>
						<p className={styles.heroDescription}>
							{tree.description?.trim() || t("library.noDescription")}
						</p>
					</div>

					<div className={styles.heroActions}>
						<button
							aria-label={isLiked ? t("view.unlike") : t("view.like")}
							aria-pressed={isLiked}
							className={isLiked ? styles.statPillLiked : styles.statPillButton}
							disabled={isLikePending}
							onClick={toggleLike}
							type="button">
							<ViewIcon name="heart" />
							<span className={styles.statLabel}>{t("view.like")}</span>
							<span className={styles.statValue}>{formatCount(likeCount)}</span>
						</button>
						<button
							aria-label={t("view.share")}
							className={styles.statPillButton}
							disabled={isDraft}
							onClick={canManage ? openShareDialog : onShareClick}
							title={isDraft ? t("view.shareDraftDisabled") : undefined}
							type="button">
							<ViewIcon name="share" />
							<span className={styles.statLabel}>{t("view.share")}</span>
						</button>
						{isDraft && canManage && <PublishMenu onTreeUpdated={onTreeSaved} treeId={tree.id} />}
						<span className={styles.statPill}>
							<ViewIcon name="eye" />
							<span className={styles.statValue}>{formatCount(tree.viewCount)}</span>
							<span className={styles.statLabel}>{t("view.views")}</span>
						</span>
						{canEdit && (
							<button className={styles.editButton} onClick={goToEdit} type="button">
								<ViewIcon name="pencil" />
								<span>{t("view.edit")}</span>
							</button>
						)}
						{!isOwner && tree.visibility === "Public" && (
							<button className={styles.statPillButton} onClick={openReportDialog} type="button">
								<span className={styles.statLabel}>{t("view.report")}</span>
							</button>
						)}
						{!isOwner && (
							<button
								className={isStarred ? styles.starredButton : styles.starButton}
								disabled={isStarPending}
								onClick={toggleStar}
								type="button">
								<ViewIcon name={isStarred ? "starFilled" : "star"} />
								{isStarred ? t("view.starred") : t("view.star")}
							</button>
						)}
					</div>
				</section>

				<div className={styles.metaRow}>
					<Link
						className={styles.metaOwner}
						title={t("view.viewProfile", { name: ownerName })}
						to={getUserProfilePath(tree.ownerUserId)}>
						<span className={styles.ownerAvatar}>{getInitials(ownerName) || "TT"}</span>
						{t("view.createdBy", { name: ownerName })}
					</Link>
					{collaborators.length > 1 && (
						<>
							<span className={styles.metaDot}>•</span>
							<CollaboratorStack
								collaborators={collaborators}
								label={t("view.collaboratorsWithCount", { count: collaborators.length })}
								onClick={openCollaborators}
							/>
						</>
					)}
					<span className={styles.metaDot}>•</span>
					<span className={styles.metaItem}>
						<ViewIcon name={getVisibilityIcon(tree.visibility)} />
						{tree.visibility}
					</span>
					<span className={styles.metaDot}>•</span>
					{/* Always shown, zero included, like the comment and star counts beside it — an absent
					    item would read as "unknown" rather than "none". Straight from the tree row that is
					    already loaded; never counted off the entities array, which is empty while it loads. */}
					<span className={styles.metaItem}>
						<ViewIcon name="tree" />
						{t("view.entityCount", { count: tree.entityCount })}
					</span>
					<span className={styles.metaDot}>•</span>
					<button className={styles.commentsToggle} onClick={openComments} type="button">
						<ViewIcon name="comment" />
						{t("view.commentsWithCount", { count: commentCount })}
					</button>
					<span className={styles.metaDot}>•</span>
					<span className={styles.metaItem}>
						<ViewIcon name="star" />
						{t("view.stars", { count: starCount })}
					</span>
				</div>

				{!isCanvasLoading && hasEntities && (
					<div className={styles.relationGroupBar}>
						<RelationGroupChips onChange={setRelationGroup} value={relationGroup} />
					</div>
				)}

				{/* Three states, kept apart rather than nested: still fetching, drawable, or genuinely
				    empty. "No entities yet" must never stand in for a tree whose entities are still on
				    their way — on a large tree that reads as data loss. */}
				<section aria-label={t("editor.canvasLabel")} className={styles.canvas}>
					{isCanvasLoading && (
						<div className={styles.canvasLoading}>
							<Spinner label={t("view.loadingEntities")} />
						</div>
					)}

					{!isCanvasLoading && hasEntities && (
						<TreeGraph
							entities={entities}
							onBackgroundClick={onCanvasBackgroundClick}
							onSelectEntity={onSelectEntity}
							relationGroup={relationGroup}
							relations={relations}
							selectedEntityId={null}
							showMiniMap={false}
						/>
					)}

					{!isCanvasLoading && !hasEntities && (
						<div className={styles.canvasEmpty}>
							<img
								alt=""
								className={styles.canvasMark}
								src={`${process.env.PUBLIC_URL}/assets/images/logo.png`}
							/>
							<h2>{t("view.noEntitiesTitle")}</h2>
							<p>{t("view.noEntitiesSubtitle")}</p>
							{canEdit && (
								<button className={styles.canvasEditButton} onClick={goToEdit} type="button">
									<ViewIcon name="pencil" />
									<span>{t("view.startBuilding")}</span>
								</button>
							)}
						</div>
					)}
				</section>
			</main>

			{isCommentsOpen && (
				<>
					<button
						aria-label={t("view.closeComments")}
						className={styles.commentsScrim}
						onClick={closeComments}
						type="button"
					/>
					<aside aria-label={t("view.comments")} className={styles.commentsPanel}>
						<header className={styles.commentsHeader}>
							<h2>{t("view.commentsWithCount", { count: commentCount })}</h2>
							<button
								aria-label={t("view.closeComments")}
								className={styles.iconButton}
								onClick={closeComments}
								type="button">
								<ViewIcon name="close" />
							</button>
						</header>

						<div className={styles.commentsBody}>
							{!commentsEnabled && <p className={styles.commentsEmpty}>{t("view.commentsDisabled")}</p>}

							{commentsEnabled && hasSession && (
								<div className={styles.commentComposer}>
									<UserAvatar
										className={styles.composerAvatar}
										imageUrl={user?.profileImageUrl}
										initials={viewerInitials}
									/>
									<div className={styles.composerFields}>
										<textarea
											aria-label={t("view.commentPlaceholder")}
											maxLength={fieldLimits.comment}
											onChange={onCommentChange}
											placeholder={t("view.commentPlaceholder")}
											value={commentBody}
										/>
										<button
											className={styles.postButton}
											disabled={isPosting || commentBody.trim().length === 0}
											onClick={onPostComment}
											type="button">
											<ViewIcon name="send" />
											<span>{t("view.post")}</span>
										</button>
									</div>
								</div>
							)}

							{commentsEnabled && !hasSession && (
								<button className={styles.commentsSignIn} onClick={goToSignIn} type="button">
									{t("view.signInToComment")}
								</button>
							)}

							{comments.length === 0 ? (
								commentsEnabled && <p className={styles.commentsEmpty}>{t("view.noComments")}</p>
							) : (
								<div className={styles.commentList}>
									{comments.map((comment: CommentDto) => (
										<CommentItem
											key={comment.id}
											canModerate={isOwner}
											canReply={commentsEnabled && hasSession}
											comment={comment}
											isReply={false}
											onDeleteComment={onDeleteComment}
											onEditComment={onEditComment}
											onPostReply={onPostReply}
											onToggleLike={onToggleCommentLike}
											shareUrl={shareUrl}
										/>
									))}
								</div>
							)}
						</div>
					</aside>
				</>
			)}

			{isEditOpen && (
				<EditTreeDialog
					canCreatePrivateTrees={canCreatePrivateTrees}
					canDelete={isOwner}
					onClose={closeEditDialog}
					onDeleted={onTreeDeleted}
					onSaved={onTreeSaved}
					tree={tree}
				/>
			)}

			{isShareOpen && (
				<ShareTreeDialog
					onClose={closeShareDialog}
					onCopyLink={onShareClick}
					onTreeUpdated={onTreeSaved}
					tree={tree}
				/>
			)}

			{isReportOpen && <ReportTreeDialog onClose={closeReportDialog} treeId={tree.id} treeTitle={tree.title} />}

			{isCollaboratorsOpen && <CollaboratorsDialog collaborators={collaborators} onClose={closeCollaborators} />}
		</div>
	);
};

export default TreeViewPage;
